<script setup lang="ts">
  import { computed } from "vue";
  import { Text, IconMenuAlarm } from "@ui-kit";
  import { useDeviceInfo } from "@plugins";
  import { useUserInfoStore, useWidgetsStore } from "@src/store";
  import { useProfileOptions } from "@src/hooks";

  const {
    disableWriteAction = false,
    disableCheckAction = false,
    disableEncodingAction = false,
  } = defineProps<{
    disableWriteAction?: boolean;
    disableCheckAction?: boolean;
    disableEncodingAction?: boolean;
  }>();

  const { deviceInfo } = useDeviceInfo();
  const { hasPermission } = useProfileOptions();
  const user_info_store = useUserInfoStore();
  const widgets_store = useWidgetsStore();

  const isEncodingAvailableAction = computed(
    () => user_info_store.shop?.config.options.rfid_alarm && !disableEncodingAction,
  );
  const isCheckAvailableAction = computed(() => user_info_store.shop?.config.options.rfid_alarm && !disableCheckAction);
  const isWriteAvailableAction = computed(
    () => user_info_store.shop?.config.options.dc_rfid_alarm && !disableWriteAction,
  );
  const isAlarmingAvailableActions = computed(() => {
    if (!hasPermission("mobile.item_alarm")) return false;
    if (!deviceInfo?.value.isRfidReader) return false;

    return isEncodingAvailableAction.value || isCheckAvailableAction.value || isWriteAvailableAction.value;
  });
</script>

<template>
  <div v-if="isAlarmingAvailableActions" class="main-page-action-alarming">
    <div class="main-page-action-alarming__header">
      <IconMenuAlarm />
      <Text font="body-regular-18" :wrap="false">{{ $t("Работа с алармами") }}</Text>
    </div>
    <div class="main-page-action-alarming__actions">
      <button
        v-if="isEncodingAvailableAction"
        class="main-page-action-alarming__action"
        @click="widgets_store.open('alarming')"
        data-testid="action-alarming"
      >
        {{ $t("Кодировка") }}
      </button>
      <button
        v-if="isCheckAvailableAction"
        class="main-page-action-alarming__action"
        @click="widgets_store.open('check_alarming')"
        data-testid="action-check-alarming"
      >
        {{ $t("Проверка") }}
      </button>
      <button
        v-if="isWriteAvailableAction"
        class="main-page-action-alarming__action"
        @click="widgets_store.open('write_alarm')"
        data-testid="action-write-alarming"
      >
        {{ $t("Запись") }}
      </button>
    </div>
  </div>
</template>

<style scoped>
  .main-page-action-alarming {
    display: flex;
    flex-direction: column;
    border-radius: 16px;
    background-color: var(--sf-base-background-white);
  }

  .main-page-action-alarming__header {
    display: flex;
    gap: 8px;
    align-items: center;
    padding: 12px;
  }

  .main-page-action-alarming__actions {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 4px;
    padding: 0 8px 8px;
  }

  /* 1 элемент - занимает всю строку */
  .main-page-action-alarming__actions > *:only-child {
    grid-column: 1 / -1;
  }

  /* 3 элемент (когда он последний) - занимает всю вторую строку */
  .main-page-action-alarming__actions > *:nth-child(3):nth-last-child(1) {
    grid-column: 1 / -1;
  }

  .main-page-action-alarming__action {
    border-radius: 12px;
    background-color: var(--sf-base-background-primary);
    padding: 12px 8px;
    height: 44px;
  }
</style>
